import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Snackbar, Alert, MenuItem, Select, InputLabel, FormControl, CircularProgress, Fade, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import 'react-quill/dist/quill.snow.css';
import dayjs from 'dayjs';
import { ToastContainer, toast } from 'react-toastify';
import { getAllDepartments } from '../../api/department-api';
import { getSubdepartment, createSubDepartment, updateSubDepartment, deleteSubDepartment } from '../../api/subDepartment-api';
import { storage } from '../../firebaseconfig';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';

const PostManagement = () => {
  const [departments, setDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [currentDepartment, setCurrentDepartment] = useState('');
  const [posts, setPosts] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [postState, setPostState] = useState({ id: null, name: '', description: '', departmentId: '', image: '', pdfUrl: '', author: '', createdAt: '' });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  // const [snackbarMessage, toast] = useState('');
  const [loading, setLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const data = await getAllDepartments();
        setDepartments(data);
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };

    fetchDepartments();
  }, []);

  useEffect(() => {
    if (currentDepartment) {
      const fetchSubDepartments = async () => {
        try {
          const data = await getSubdepartment(currentDepartment);
          setSubDepartments(data);
          setPosts(data);
        } catch (error) {
          console.error('Error fetching sub-departments:', error);
        }
      };
      fetchSubDepartments();
    }
  }, [currentDepartment]);

  const handleDepartmentChange = (event) => {
    setCurrentDepartment(event.target.value);
  };

  const handleOpenDialog = (post = { id: null, name: '', description: '', departmentId: '', image: '', pdfUrl: '', author: '', createdAt: '' }) => {
    setPostState(post);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setPostState({ id: null, name: '', description: '', departmentId: '', image: '', pdfUrl: '', author: '', createdAt: '' });
  };

  const handleSavePost = async () => {
    if (!postState.name || !postState.description || !postState.departmentId) {
      toast('Title, Description, and Department are required.');
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);

    try {
      if (postState.id) {
        // Update existing post
        await updateSubDepartment(postState.id, postState);
        setPosts((prevPosts) =>
          prevPosts?.map((post) => (post.id === postState.id ? { ...postState } : post))
        );
        toast('Post updated successfully.');
      } else {
        // Create new post
        const newPost = await createSubDepartment(postState);
        setPosts((prevPosts) => [...prevPosts, newPost]);
        toast('Post added successfully.');
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving post:', error);
      toast('Failed to save post.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (id) => {
    try {
      await deleteSubDepartment(id);
      setPosts((prevPosts) => prevPosts.filter((post) => post.id !== id));
      toast('Post deleted successfully.');
    } catch (error) {
      toast.error('Error deleting post:', error);
      toast('Failed to delete post.');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const storageRef = ref(storage, `images/${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.log('Upload is ' + progress + '% done');
          setImageUploading(true);
        },
        (error) => {
          console.error('Error uploading image:', error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            setPostState({ ...postState, image: downloadURL });
            setImageUploading(false);
            console.log('Image uploaded successfully:', downloadURL);
          });
        }
      );
    }
  };

  const handlePdfUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const storageRef = ref(storage, `pdfs/${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          setPdfUploading(true);
        },
        (error) => {
          console.error('Error uploading PDF:', error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            setPostState({ ...postState, pdfUrl: downloadURL });
            setPdfUploading(false);
            console.log('PDF uploaded successfully:', downloadURL);
          });
        }
      );
    }
  };

  return (
    <Box sx={{ p: 4, py: "10vh" }}>
      <Typography variant="h4" gutterBottom align="center" sx={{ fontWeight: 'bold', mb: 3 }}>
        Post Management
      </Typography>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Select Department</InputLabel>
        <Select
          value={currentDepartment || ''}
          onChange={handleDepartmentChange}
          label="Select Department"
        >
          {departments.map(department => (
            <MenuItem key={department.id} value={department.id}>
              {department.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        color="primary"
        startIcon={<AddCircleOutlineIcon />}
        onClick={() => handleOpenDialog()}
        sx={{ mb: 3 }}
      >
        Add New Post
      </Button>

      <Fade in={true}>
        <TableContainer component={Paper} elevation={8} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#3f51b5', color: '#fff' }}>
              <TableRow>
                <TableCell sx={{ color: '#fff' }}><strong>Title</strong></TableCell>
                <TableCell sx={{ color: '#fff' }}><strong>Description</strong></TableCell>
                <TableCell sx={{ color: '#fff' }}><strong>Definition</strong></TableCell>
                <TableCell sx={{ color: '#fff' }}><strong>Author</strong></TableCell>
                <TableCell sx={{ color: '#fff' }}><strong>Created At</strong></TableCell>
                <TableCell sx={{ color: '#fff' }}><strong>Image</strong></TableCell>
                <TableCell sx={{ color: '#fff' }}><strong>PDF</strong></TableCell>
                <TableCell sx={{ color: '#fff' }}><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {console.log("post==============>", posts)}
              {posts?.map((post) => (
                <Fade key={post.id} in={true}>
                  <TableRow>
                    <TableCell>{post.name}</TableCell>
                    <TableCell>
                      {post.description.length > 50 ? `${post.description.slice(0, 50)}...` : post.description}
                    </TableCell>
                    <TableCell>
                      {post.definition}
                    </TableCell>
                    <TableCell>{post.author}</TableCell>
                    <TableCell>{dayjs(post.createdAt).format('YYYY-MM-DD HH:mm:ss')}</TableCell>
                    <TableCell>
                      {post.image && <img src={post.image} alt="Post Thumbnail" style={{ maxWidth: '100px', height: 'auto' }} />}
                    </TableCell>
                    <TableCell>
                      {post.pdfUrl ? (
                        <a href={post.pdfUrl} target="_blank" rel="noopener noreferrer">
                          View PDF
                        </a>
                      ) : (
                        <span>No PDF available</span>
                      )}
                    </TableCell>

                    <TableCell>
                      <Tooltip name="Edit">
                        <IconButton onClick={() => handleOpenDialog(post)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip name="Delete">
                        <IconButton color="error" onClick={() => handleDeletePost(post.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                </Fade>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Fade>


      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            backgroundColor: '#1976d2',
            color: '#fff',
            fontWeight: 'bold',
            fontSize: '1.25rem'
          }}
        >
          {postState.id ? 'Edit Post' : 'Add New Post'}
        </DialogTitle>
        <DialogContent
          sx={{
            padding: '20px',
            backgroundColor: '#f5f5f5'
          }}
        >
          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Select Department</InputLabel>
              <Select
                value={postState.departmentId || ''}
                onChange={(e) => setPostState({ ...postState, departmentId: e.target.value })}
                label="Select Department"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px'
                  }
                }}
              >
                {departments.map(department => (
                  <MenuItem key={department.id} value={department.id}>
                    {department.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <TextField
            fullWidth
            label="Title"
            variant="outlined"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
            value={postState.name}
            onChange={(e) => setPostState({ ...postState, name: e.target.value })}
          />
          <TextField
            fullWidth
            label="Write the post description..."
            variant="outlined"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                height: "20vh"
              }
            }}
            value={postState.description}
            onChange={(e) => setPostState({ ...postState, description: e.target.value })}
          />
          <TextField
            fullWidth
            label="Write the post Definition..."
            variant="outlined"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                height: "20vh"
              }
            }}
            value={postState.definition}
            onChange={(e) => setPostState({ ...postState, definition: e.target.value })}
          />

          <TextField
            fullWidth
            label="Author"
            variant="outlined"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px'
              }
            }}
            value={postState.author}
            onChange={(e) => setPostState({ ...postState, author: e.target.value })}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="contained"
              component="label"
              sx={{
                borderRadius: '8px',
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#115293'
                }
              }}
            >
              Upload Image
              <input hidden accept="image/*" type="file" onChange={handleImageUpload} />
            </Button>
            {imageUploading ? (
              <CircularProgress />
            ) : (
              postState.image && <img src={postState.image} alt="Uploaded" style={{ width: '100%', maxHeight: '150px', borderRadius: '8px', objectFit: 'cover' }} />
            )}
            <Button
              variant="contained"
              component="label"
              sx={{
                borderRadius: '8px',
                backgroundColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#115293'
                }
              }}
            >
              Upload PDF
              <input hidden accept="application/pdf" type="file" onChange={handlePdfUpload} />

            </Button>
            {pdfUploading ? (
              <CircularProgress />
            ) : (
              postState.pdfUrl && (
                <a href={postState.pdfUrl} target="_blank" rel="noopener noreferrer">
                  View PDF
                </a>
              )
            )},
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            padding: '16px',
            backgroundColor: '#f5f5f5',
            borderTop: '1px solid #ddd'
          }}
        >
          <Button onClick={handleCloseDialog} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSavePost} color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      <ToastContainer />
    </Box>
  );
};

export default PostManagement;
